import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import Util, { Order } from '@/js/common/Util';
import ReactPaginate from 'react-paginate';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import GroupCheckBox, { checkStateType } from '@/js/common/ui/input/GroupCheckBox';
import CheckBox from './input/CheckBox';
import AppContext from '../AppContext';
import styled from "styled-components";
import Span from './Span';

const StylingPaginate = styled.div<{paginatestyle?: paginateStyleProps}>`
    ul {
        display: flex;
        list-style-type: none;
        padding: 0;
        margin: 0;
    }
    ul li {
        margin-right: ${(props) => props.paginatestyle?.buttonMarginLeft}px;
        margin-left: ${(props) => props.paginatestyle?.buttonMarginRight}px;
    }
    ul li a {
        padding: 0 5px;
        min-width: ${(props) => props.paginatestyle?.buttonWidth}px;
        height: ${(props) => props.paginatestyle?.buttonHeight}px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-sizing: border-box;
        font-size: ${(props) => props.paginatestyle?.fontSize}px;
        color: ${(props) => props.paginatestyle?.color};
        letter-spacing: ${(props) => props.paginatestyle?.letterSpacing}px;
        font-family: ${(props) => props.paginatestyle?.fontFamily};
        outline: none;
        border-radius: ${(props) => props.paginatestyle?.borderRadius}px;
    }
    ul li.previous a,
    ul li.next a,
    ul li.break a {
        border-color: transparent;
    }
    ul li.selected a, ul li:hover:not(.previous):not(.next):not(.break) a, ul li:focus a {
        background: ${(props) => props.paginatestyle?.buttonOnColor};
        color: #fff;
        font-family: fm-c;
    }
    ul li.disabled a {
        color: #cecece;
    }
    ul li.disable,
    ul li.disabled a {
        cursor: default;
    }
`;

const chkBoxFldWidth = 30; // 체크박스 필드 너비
const chkSetupInfoValidation = (tableInfo: tableInfoProps) => {
    if (Util.isEmpty(tableInfo)) {
        alert("테이블 설정 정보가 없습니다.");
        return false;
    }
    if (Util.isEmpty(tableInfo.maxRowNums)) {
        alert("테이블 항목수 배열 정보가 없습니다.");
        return false;
    }
    return true;
};

interface EnhancedTableProps {
    chkBoxExist: boolean; // 체크박스 배치 여부
    numSelected: number; // 체크한 row count
    rowCount: number; // 전체 row count
    onSelectAllClick: (checkState: checkStateType) => void; // 전체 체크 이벤트
    order: Order;
    orderBy: string;
    onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
    tableInfo: tableInfoProps;
    visible: boolean;
}
function EnhancedTableHead(props: EnhancedTableProps) {
    const { chkBoxExist, numSelected, rowCount, onSelectAllClick, order, orderBy, onRequestSort, tableInfo, visible } = props;
    const createSortHandler = (property: any) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };
    let left = 0;
    if (chkBoxExist) {
        left += chkBoxFldWidth;
    }
    const fixedFldStyle = {
        position: "sticky",
        zIndex: 1
    }
    interface rowThProps {
        grpFldSeq?: number
        grpFldlabel?: string
        rowSpan: number
        colSpan: number
        fldInf: fldInfsProps
        grpFldFlag: boolean
        grpFldWidth?: number
    }
    const trList: rowThProps[][] = [];
    let firstGrpFldNm: string = "";
    if (tableInfo.grpFldInfs) {
        const firstTrThList: rowThProps[] = [];
        const secondTrThList: rowThProps[] = [];
        for (const fldInf of tableInfo.fldInfs) {
            let childFldFlag = false;
            let grpFldNm = "";
            let childFlds: string[] = [];
            let grpFldColspan = 1;
            for (const grpFldInf of tableInfo.grpFldInfs) {
                if (fldInf.fldNm) {
                    if (grpFldInf.childFlds.indexOf(fldInf.fldNm) !== -1) {
                        childFldFlag = true;
                        grpFldNm = grpFldInf.title;
                        childFlds = grpFldInf.childFlds;
                        grpFldColspan = grpFldInf.childFlds.length;
                        break;
                    }
                }
            }
            if (!childFldFlag) {
                firstTrThList.push({
                    rowSpan: childFldFlag ? 1 : 2,
                    colSpan: 1,
                    fldInf: fldInf,
                    grpFldFlag: false
                });
            } else {
                let dupChk = false;
                for (const firstTrTh of firstTrThList) {
                    if (firstTrTh.grpFldlabel === grpFldNm) {
                        dupChk = true;
                        break;
                    }
                }
                if (!dupChk) {
                    let grpFldWidth = 0;
                    for (const fldInf of tableInfo.fldInfs) {
                        if (!fldInf.fldNm) {
                            continue;
                        }
                        if (!fldInf.width) {
                            continue;
                        }
                        if (childFlds.indexOf(fldInf.fldNm) !== -1) {
                            grpFldWidth += fldInf.width;
                        }
                    }
                    if (firstGrpFldNm === "") {
                        firstGrpFldNm = grpFldNm;
                    }
                    firstTrThList.push({
                        grpFldlabel: grpFldNm,
                        rowSpan: 1,
                        colSpan: grpFldColspan,
                        fldInf: fldInf,
                        grpFldFlag: true,
                        grpFldWidth: grpFldWidth
                    });
                }
            }
        }
        trList.push(firstTrThList);
        for (const grpFldInf of tableInfo.grpFldInfs) {
            for (const childFld of grpFldInf.childFlds) {
                for (const fldInf of tableInfo.fldInfs) {
                    if (childFld === fldInf.fldNm) {
                        secondTrThList.push({
                            rowSpan: 1,
                            colSpan: 1,
                            fldInf: fldInf,
                            grpFldFlag: false
                        });
                        break;
                    }
                }
            }
        }
        trList.push(secondTrThList);
    } else {
        const firstTrThList: rowThProps[] = [];
        for (const fldInf of tableInfo.fldInfs) {
            firstTrThList.push({
                rowSpan: 1,
                colSpan: 1,
                fldInf: fldInf,
                grpFldFlag: false
            });
        }
        trList.push(firstTrThList);
    }

    return (
        <TableHead
            style={{
                visibility: (visible ? "visible" : "collapse"),
                borderBottom: "1px solid #DDDDDD"
            }}
        >
            {trList.map((trThList: rowThProps[], idx: number) => {
                let checkState: checkStateType = "noCheck";
                if (numSelected === 0) {
                    checkState = "noCheck";
                } else if (numSelected === rowCount) {
                    checkState = "allCheck";
                } else {
                    checkState = "check";
                }
                return (
                    <TableRow key={idx} sx={[
                        tableInfo["styleType"].tableHeadRow,
                        { height: !tableInfo["theadTrHeight"] ? tableInfo["tbodyTrHeight"] : tableInfo["theadTrHeight"] + "px" }
                    ]}>
                        {idx === 0 && chkBoxExist ?
                            <TableCell
                                rowSpan={tableInfo.grpFldInfs ? 2 : 1}
                                padding="checkbox"
                                sx={[
                                    tableInfo["styleType"].tableHeadChkBoxCell,
                                    {
                                        position: (!tableInfo["fixFldCnt"] ? -1 : tableInfo["fixFldCnt"]) > 0 ? "sticky" : null,
                                        left: 0,
                                        zIndex: 1,
                                        backgroundColor: (!tableInfo["fixFldCnt"] ? -1 : tableInfo["fixFldCnt"]) > 0 ? "#FDFCFC" : null
                                    }
                                ]}
                            >
                                <div className='w100p h100p dpFlx aiC jcC'>
                                    <GroupCheckBox
                                        checkState={checkState}
                                        onChange={() => onSelectAllClick(checkState)}
                                        buttonColor={AppContext.themeColor}
                                        borderOnColor="#111"
                                        borderColor="#999"
                                        borderWidth={2}
                                    />
                                </div>
                            </TableCell>
                        : <></>}
                        {trThList.map((trThInfo: rowThProps, thIdx: number) => {
                            if (trThInfo.grpFldFlag) {
                                return (
                                    <TableCell key={thIdx} colSpan={trThInfo.colSpan} align="center" sx={[
                                        tableInfo["styleType"].tableHeadCell, {
                                            overflow: "hidden",
                                            width: trThInfo.grpFldWidth
                                        },
                                        idx === 0 && (thIdx + 1) + (chkBoxExist ? 1 : 0) <= (tableInfo["fixFldCnt"] ? tableInfo["fixFldCnt"] : 0) ? fixedFldStyle : {}
                                    ]}>
                                        {firstGrpFldNm === trThInfo.grpFldlabel ? trThInfo.grpFldlabel :
                                            <div className='bdl1 bdcC5C5C5'>
                                                {trThInfo.grpFldlabel}
                                            </div>
                                        }
                                    </TableCell>
                                )
                            }
                            const fldInf = trThInfo.fldInf;
                            if (!fldInf.type) {
                                return null;
                            }
                            const currLeft = left;
                            left = left + (fldInf.width ? fldInf.width : 0);
                            let tableSortLabel;
                            if (["text", "comma", "phone", "code", "date", "time", "syscode"].indexOf(fldInf.type || "") !== -1 || (fldInf.type === "el" && Util.isNotEmpty(fldInf.fldNm))) {
                                tableSortLabel =
                                    <TableSortLabel
                                        active={orderBy === fldInf.fldNm}
                                        direction={orderBy === fldInf.fldNm ? order : 'asc'}
                                        onClick={createSortHandler(fldInf.fldNm)}
                                        sx={{
                                            ".MuiTableSortLabel-icon": { fontSize: 16, marginLeft: 0 },
                                        }}
                                    >
                                        {fldInf.label}
                                        {orderBy === fldInf.fldNm ? (
                                            <Box component="span" sx={visuallyHidden}>
                                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                            } else {
                                tableSortLabel = fldInf.label;
                            }
                            return (
                                <TableCell
                                    key={thIdx}
                                    rowSpan={trThInfo.rowSpan}
                                    colSpan={trThInfo.colSpan}
                                    align={fldInf.headAlign}
                                    sortDirection={orderBy === fldInf.fldNm ? order : false}
                                    sx={[
                                        tableInfo["styleType"].tableHeadCell, {
                                            width: fldInf.wAuto ? null : fldInf.width,
                                            minWidth: fldInf.wAuto ? fldInf.width : null,
                                            left: currLeft
                                        },
                                        idx === 0 && (thIdx + 1) + (chkBoxExist ? 1 : 0) <= (tableInfo["fixFldCnt"] ? tableInfo["fixFldCnt"] : 0) ? fixedFldStyle : {}
                                    ]}
                                >
                                    {tableSortLabel}
                                </TableCell>
                            )
                        })}
                    </TableRow>
                )
            })}
        </TableHead>
    );
}

interface ordFldProps {
    direction: Order
    name: string
}
interface checkedHookParamProps {
    checkDataArr: any[]
    setCheckDataArr: React.Dispatch<React.SetStateAction<any[]>>
}
export interface fldInfsProps {
    type: "text" | "code" | "phone" | "el" | "comma" | "seq" | "date" | "time" | "syscode"
    fldNm?: string
    width?: number
    headAlign?: "left" | "center" | "right"
    dataAlign?: "left" | "center" | "right"
    label?: string
    catCode?: string
    wAuto?: boolean
    el?: Function
    dfltVal?: string
    class?: string
    func?: Function
}
interface grpFldInfProps {
    title: string
    childFlds: string[]
}
interface paginateStyleProps {
    buttonOnColor: string
    buttonWidth: number
    buttonHeight: number
    buttonMarginLeft: number
    buttonMarginRight: number
    fontSize: number
    color: string
    letterSpacing: number
    fontFamily: string
    borderRadius: number
}
interface tableInfoProps {
    fldInfs: fldInfsProps[]
    grpFldInfs?: grpFldInfProps[]
    dataArr: any[]
    ordFld?: ordFldProps
    maxRowNums: number[]
    srchVsb?: boolean
    styleType: any
    tableHeadVsb?: boolean
    bodyHeight?: number
    heightType?: "min" | "fix"
    fixFldCnt?: number
    theadTrHeight?: number
    tbodyTrHeight: number
    rowClickFunc?: Function
    doubleClickFunc?: Function
    rowClickBgcFix?: boolean
    rowClickAuto?: boolean
    remainArea?: boolean
    footerVsb?: boolean
    paginateClass?: string
    checkedHookParam?: checkedHookParamProps
    apiFunc?: Function
    totalCnt?: number
    itemsPerPage?: number
    itemOffset?: number
    paginateStyle?: paginateStyleProps
}
interface GridTableProps {
    tableInfo: tableInfoProps
}
const GridTable = ({ tableInfo }: GridTableProps) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const validationResult = chkSetupInfoValidation(tableInfo);
    const [order, setOrder] = React.useState<Order>(tableInfo.ordFld ? tableInfo.ordFld.direction : "asc");
    const [orderBy, setOrderBy] = React.useState<string>(tableInfo.ordFld ? tableInfo.ordFld.name : "");
    const chkBoxExist = Util.isNotEmpty(tableInfo["checkedHookParam"]); // 체크박스 표출여부 : 체크 핸들러 훅이 파라미터로 존재할 경우에 표출
    const [filterDataArr, setFilterDataArr]: any[] = React.useState<any[]>([]);
    const clickTimeOut = React.useRef<any>(null);

    let colSpan = chkBoxExist ? 1 : 0; // td colspan, 체크박스 표출 시 + 1
    tableInfo["fldInfs"].forEach((item: any) => {
        if (Util.isNotEmpty(item.type)) {
            colSpan = colSpan + 1;
        }
    });

    const headCells: readonly fldInfsProps[] = tableInfo["fldInfs"];
    const [itemsPerPage, setItemsPerPage] = React.useState<number>(tableInfo["maxRowNums"][0]); // 한페이지에 포함시킬 ROW 수
    const [itemOffset, setItemOffset] = React.useState<number>(0); // 페이지 번호
    const [rowCnt, setRowCnt] = React.useState<number>(0); // 행 갯수
    const pageCount = Math.ceil((tableInfo["totalCnt"] ? tableInfo["totalCnt"] : filterDataArr.length) / itemsPerPage); // 페이지 수
    const [currentItems, setCurrentItems] = React.useState<any[]>([]);// 현재 페이지에 표출할 데이터 배열
    const [focusIndex, setFocusIndex] = React.useState<string>(""); // 포커싱 row 인덱스

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setItemOffset(0);
        if (tableInfo["apiFunc"]) {
            tableInfo["apiFunc"](1, itemsPerPage, property, isAsc ? 'desc' : 'asc');
        }
        if (scrollRef.current !== null) {
            scrollRef.current.scrollTop = 0;
        }
        if (tableInfo["checkedHookParam"]) {
            const setCheckDataArr = tableInfo["checkedHookParam"]["setCheckDataArr"];
            setCheckDataArr([]);
        }
    };

    const handlePageClick = (event: any) => { // 하단 버튼 이벤트 함수 : 이전/다음, 1/2/3/4/5...
        if (tableInfo["apiFunc"]) {
            tableInfo["apiFunc"](event.selected + 1, itemsPerPage, orderBy, order);
        }
        setItemOffset(event.selected);
        if (scrollRef.current !== null) {
            scrollRef.current.scrollTop = 0;
        }
        if (tableInfo["checkedHookParam"]) {
            const setCheckDataArr = tableInfo["checkedHookParam"]["setCheckDataArr"];
            setCheckDataArr([]);
        }
    };

    const handleSelectAllClick = (checkState: checkStateType) => {
        if (!tableInfo["checkedHookParam"]) {
            return;
        }
        const setCheckDataArr = tableInfo["checkedHookParam"]["setCheckDataArr"];
        if (checkState === "noCheck") {
            const newSelected = currentItems.map((n: any) => n);
            setCheckDataArr(newSelected);
        } else {
            setCheckDataArr([]);
        }
    };

    const handleClick = (row: any) => {
        if (!tableInfo["checkedHookParam"]) {
            return;
        }
        const checkDataArr = tableInfo["checkedHookParam"]["checkDataArr"];
        const setCheckDataArr = tableInfo["checkedHookParam"]["setCheckDataArr"];
        let isItemSelected = false; // 선택리스트 배열에 존재하면 제외해야 함
        for (const checkData of checkDataArr) {
            if (row["checkBoxPk"] === checkData["checkBoxPk"]) {
                isItemSelected = true;
                break;
            }
        }

        const newItems: any = [];
        if (isItemSelected) { // 해당객체 제거
            for (const checkData of checkDataArr) {
                let addFlag = false;
                if (row["checkBoxPk"] !== checkData["checkBoxPk"]) {
                    addFlag = true;
                }
                if (addFlag) {
                    newItems.push(checkData);
                }
            }
        } else { // 해당객체 추가
            checkDataArr.push(row);
            for (const currentItem of currentItems) {
                let addFlag = false;
                for (const checkData of checkDataArr) {
                    if (currentItem["checkBoxPk"] === checkData["checkBoxPk"]) {
                        addFlag = true;
                    }
                }
                if (addFlag) {
                    newItems.push(currentItem);
                }
            }
        }
        setCheckDataArr(newItems);
    };

    React.useEffect(() => {
        if (Util.isNotEmpty(orderBy)) {
            return;
        }
        if (tableInfo.ordFld) {
            setOrder(tableInfo.ordFld.direction);
            setOrderBy(tableInfo.ordFld.name);
        }
    }, [tableInfo.ordFld, orderBy]);

    React.useEffect(() => {
        if (tableInfo.itemOffset === undefined) {
            return;
        }
        setItemOffset(tableInfo.itemOffset);
    }, [tableInfo.itemOffset]);

    React.useEffect(() => {
        if (!tableInfo.itemsPerPage) {
            return;
        }
        setItemsPerPage(tableInfo.itemsPerPage);
    }, [tableInfo.itemsPerPage]);

    React.useEffect(() => {
        if (!tableInfo.rowClickAuto) {
            return;
        }
        if (!tableInfo.rowClickFunc) {
            return;
        }
        if (currentItems.length > 0) {
            tableInfo.rowClickFunc(currentItems[0]);
            setFocusIndex("0-0");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentItems]);

    React.useEffect(() => {
        if (focusIndex.split('-')[0] === itemOffset.toString()) {
            return;
        }
        setFocusIndex(itemOffset + "-" + 0);
    }, [focusIndex, itemOffset]);

    React.useEffect(() => {
        // if (scrollRef.current !== null) {
        //     scrollRef.current.scrollLeft = 0;
        // }
        // if (tableInfo.ordFld) {
        //     setOrder(tableInfo.ordFld.direction);
        //     setOrderBy(tableInfo.ordFld.name);
        // }
        if (!Array.isArray(tableInfo.dataArr)) {
            setFilterDataArr([]);
            setCurrentItems([]);
            setItemOffset(0);
        } else {
            const tempDataArr: any = [];
            for (const data of tableInfo.dataArr) {
                const tempData = {...data};
                tempData.checkBoxPk = tempDataArr.length;
                tempDataArr.push(tempData);
            }
            setFilterDataArr(tempDataArr);
            if (tableInfo.apiFunc) {
                setCurrentItems(tempDataArr);
                return;
            }
            setItemOffset((prev) => {
                const nextPageCount = Math.ceil(tempDataArr.length / itemsPerPage);
                if (nextPageCount === 0) {
                    return 0;
                }
                return prev >= nextPageCount ? nextPageCount - 1 : prev;
            });
        }
    }, [tableInfo.dataArr, tableInfo.apiFunc, itemsPerPage]);

    React.useEffect(() => {
        if (tableInfo.apiFunc) {
            return;
        }
        const tempCurrItems = Util.sortListJson(filterDataArr, order, orderBy).slice(
            itemOffset * itemsPerPage,
            itemOffset * itemsPerPage + itemsPerPage
        );
        setRowCnt(tempCurrItems.length === 0 ? 1 : tempCurrItems.length);
        setCurrentItems(tempCurrItems);
    }, [filterDataArr, order, orderBy, itemOffset, itemsPerPage, tableInfo.apiFunc]);

    if (!validationResult) {
        return (
            <></>
        );
    } else {
        return (
            <Box sx={{ width: '100%' }}>
                <Paper sx={[tableInfo["styleType"]["paper"], tableInfo["styleType"]["tableBorder"]]}>
                    <div className={'ofxA bdb1 bdcEDEDED ' + (currentItems.length === 0 ? "ofH" : "ofyA")} ref={scrollRef}>
                        <TableContainer sx={{
                            display: !tableInfo["tableHeadVsb"] ? "none" : "block",
                            overflow: "visible",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            ".MuiTable-root": { tableLayout: "fixed" }
                        }}>
                            <Table aria-labelledby="tableTitle">
                                <EnhancedTableHead
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={handleRequestSort}
                                    tableInfo={tableInfo}
                                    visible={true}
                                    chkBoxExist={chkBoxExist}
                                    rowCount={currentItems.length}
                                    numSelected={chkBoxExist ? (tableInfo["checkedHookParam"] ? tableInfo["checkedHookParam"]["checkDataArr"].length : 0) : 0}
                                    onSelectAllClick={handleSelectAllClick}
                                />
                            </Table>
                        </TableContainer>
                        <TableContainer sx={{
                            height: tableInfo["heightType"] === "fix" || Util.isEmpty(tableInfo["heightType"]) ? (tableInfo["bodyHeight"] && (tableInfo["remainArea"]) ? tableInfo["bodyHeight"] + 1 : (tableInfo["tbodyTrHeight"] * rowCnt + 2)) : "",
                            minHeight: tableInfo["heightType"] === "min" ? (tableInfo["bodyHeight"] && (tableInfo["remainArea"]) ? tableInfo["bodyHeight"] + 1 : (tableInfo["tbodyTrHeight"] * rowCnt + 2)) : "",
                            overflow: "visible",
                            position: "sticky",
                            ".MuiTable-root": { tableLayout: "fixed" }
                        }}>
                            <Table aria-labelledby="tableTitle">
                                <EnhancedTableHead
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={handleRequestSort}
                                    tableInfo={tableInfo}
                                    visible={false}
                                    chkBoxExist={chkBoxExist}
                                    rowCount={filterDataArr.length}
                                    numSelected={chkBoxExist ? (tableInfo["checkedHookParam"] ? tableInfo["checkedHookParam"]["checkDataArr"].length : 0) : 0}
                                    onSelectAllClick={handleSelectAllClick}
                                />
                                <TableBody sx={tableInfo["styleType"]["tableBody"]}>
                                    {currentItems.map((row: any, index: number) => {
                                        const rendering = () => {
                                            const resEls: any[] = [];
                                            let left = 0;
                                            if (chkBoxExist) {
                                                left += chkBoxFldWidth;
                                            }
                                            headCells.map((item: fldInfsProps, idx: number) => {
                                                const currLeft = left;
                                                left += Number(item.width);
                                                if (Util.isNotEmpty(item.type)) {
                                                    if (["text", "comma", "phone", "code", "date", "time", "syscode"].indexOf(item.type || "") !== -1) {
                                                        if (item.fldNm === undefined) {
                                                            return null;
                                                        }
                                                        let fldTxt = "";
                                                        let titleFldTxt = "";
                                                        if (item.type === "text") {
                                                            fldTxt = Util.nvl(row[item.fldNm], Util.nvl(item.dfltVal, ""));
                                                            titleFldTxt = fldTxt;
                                                        } else if (item.type === "comma") {
                                                            if (Util.isNotEmpty(row[item.fldNm])) {
                                                                const numberValue = Number(row[item.fldNm]).toFixed(0);
                                                                titleFldTxt = Util.comma(Number(row[item.fldNm]).toFixed(0));
                                                                fldTxt = Util.nvl(Util.comma(numberValue), Util.nvl(item.dfltVal, ""));
                                                            }
                                                        } else if (item.type === "phone") {
                                                            fldTxt = Util.nvl(Util.cvtPhoneStrForm(row[item.fldNm], "-"), Util.nvl(item.dfltVal, ""));
                                                            titleFldTxt = fldTxt;
                                                        } else if (item.type === "code") {
                                                            if (item.fldNm === undefined) {
                                                                return null;
                                                            }
                                                            if (item.catCode === undefined) {
                                                                return null;
                                                            }
                                                            if (Util.isNotEmpty(row[item.fldNm])) {
                                                                fldTxt = Util.nvl(Util.isEmpty(row[item.fldNm]) ? item.dfltVal : Util.getValToCode(item.catCode, row[item.fldNm].toString()), "");
                                                            }
                                                            titleFldTxt = fldTxt;
                                                        } else if (item.type === "date") {
                                                            fldTxt = Util.nvl(Util.cvtDateStrForm(row[item.fldNm], "-", "YYYYMMDD"), Util.nvl(item.dfltVal, ""));
                                                            titleFldTxt = fldTxt;
                                                        } else if (item.type === "time") {
                                                            fldTxt = Util.nvl(Util.cvtDateStrForm(row[item.fldNm], ":", "HH24MM"), Util.nvl(item.dfltVal, ""));
                                                            titleFldTxt = fldTxt;
                                                        }
                                                        return resEls.push(
                                                            <TableCell
                                                                key={index + "-" + idx}
                                                                align={item.dataAlign as 'inherit' | 'left' | 'center' | 'right' | 'justify'}
                                                                sx={[
                                                                    tableInfo["styleType"]["tableBodyCell"],
                                                                    {
                                                                        position: (idx + 1) + (chkBoxExist ? 1 : 0) <= (!tableInfo["fixFldCnt"] ? -1 : tableInfo["fixFldCnt"]) ? "sticky" : null,
                                                                        left: currLeft,
                                                                        backgroundColor: (idx + 1) + (chkBoxExist ? 1 : 0) <= (!tableInfo["fixFldCnt"] ? -1 : tableInfo["fixFldCnt"]) ? "rgb(244, 248, 249)" : null,
                                                                        padding: "0 5px !important"
                                                                    }
                                                                ]}
                                                            >
                                                                <span
                                                                    className={item.class || ''}
                                                                    onClick={() => {
                                                                        if (item.func === undefined) {
                                                                            return;
                                                                        }
                                                                        item.func(row);
                                                                    }}
                                                                    title={titleFldTxt}
                                                                >
                                                                    {fldTxt}
                                                                </span>
                                                            </TableCell>
                                                        );
                                                    } else if (item.type === "el") {
                                                        if (item.el === undefined) {
                                                            return null;
                                                        }
                                                        return resEls.push(
                                                            <TableCell
                                                                key={index + "-" + idx}
                                                                align={'center'}
                                                                sx={[
                                                                    tableInfo["styleType"]["tableBodyCell"],
                                                                    {
                                                                        position: (idx + 1) + (chkBoxExist ? 1 : 0) <= (!tableInfo["fixFldCnt"] ? -1 : tableInfo["fixFldCnt"]) ? "sticky" : null,
                                                                        left: currLeft,
                                                                        backgroundColor: (idx + 1) + (chkBoxExist ? 1 : 0) <= (!tableInfo["fixFldCnt"] ? -1 : tableInfo["fixFldCnt"]) ? "rgb(244, 248, 249)" : null
                                                                    }
                                                                ]}
                                                            >
                                                                <div className='dpFlx aiC jcC' style={{height: (Number(tableInfo.tbodyTrHeight) - 1) + "px"}}>
                                                                    {item.el(row, (index + 1))}
                                                                </div>
                                                            </TableCell>
                                                        );
                                                    } else if (item.type === "seq") {
                                                        return resEls.push(
                                                            <TableCell
                                                                key={index + "-" + idx}
                                                                align={'center'}
                                                                sx={[
                                                                    tableInfo["styleType"]["tableBodyCell"],
                                                                    {
                                                                        position: (idx + 1) + (chkBoxExist ? 1 : 0) <= (!tableInfo["fixFldCnt"] ? -1 : tableInfo["fixFldCnt"]) ? "sticky" : null,
                                                                        left: currLeft,
                                                                        backgroundColor: (idx + 1) + (chkBoxExist ? 1 : 0) <= (!tableInfo["fixFldCnt"] ? -1 : tableInfo["fixFldCnt"]) ? "rgb(244, 248, 249)" : null
                                                                    }
                                                                ]}
                                                            >
                                                                <div className='dpFlx jcC'>
                                                                    {itemOffset * itemsPerPage + index + 1}
                                                                </div>
                                                            </TableCell>
                                                        )
                                                    } else {
                                                        return null;
                                                    }
                                                } else {
                                                    return null;
                                                }
                                            });
                                            return resEls;
                                        };
                                        let isItemSelected = false;
                                        if (chkBoxExist && tableInfo["checkedHookParam"]) {
                                            for (const checkData of tableInfo["checkedHookParam"]["checkDataArr"]) {
                                                if (row["checkBoxPk"] === checkData["checkBoxPk"]) {
                                                    isItemSelected = true;
                                                    break;
                                                }
                                            }
                                        }
                                        // const labelId = `enhanced-table-checkbox-${index}`;
                                        return (
                                            <TableRow
                                                tabIndex={-1}
                                                key={index}
                                                sx={[
                                                    {
                                                        height: Util.isEmpty(tableInfo["tbodyTrHeight"]) ? "" : tableInfo["tbodyTrHeight"] + "px",
                                                        borderTop: "1px solid",
                                                        borderTopColor: "#DDDDDD",
                                                        "&:last-child": {
                                                            borderBottom: "1px solid",
                                                            borderBottomColor: "#DDDDDD",
                                                        }
                                                    },
                                                    Util.isNotEmpty(tableInfo["rowClickFunc"]) || Util.isNotEmpty(tableInfo["doubleClickFunc"]) ? {
                                                        "&:hover": {
                                                            cursor: "pointer",
                                                            backgroundColor: `rgba(${AppContext.themeColorR}, ${AppContext.themeColorG}, ${AppContext.themeColorB}, 0.8)`,
                                                        },
                                                    } : {},
                                                    { backgroundColor: tableInfo["rowClickBgcFix"] && focusIndex === itemOffset + "-" + index ? "#FAEBD7" : "white" },
                                                    {
                                                        "&.Mui-selected": {
                                                            backgroundColor: `rgba(${AppContext.themeColorR}, ${AppContext.themeColorG}, ${AppContext.themeColorB}, 0.1)`,
                                                        }
                                                    }
                                                ]}
                                                onDoubleClick={() => {
                                                    clearTimeout(clickTimeOut.current);
                                                    clickTimeOut.current = null;

                                                    if (tableInfo["doubleClickFunc"]) {
                                                        tableInfo["doubleClickFunc"](row);
                                                    }
                                                }}
                                                onClick={() => {
                                                    if (clickTimeOut.current) return;

                                                    clickTimeOut.current = setTimeout(() => {
                                                        if (tableInfo["rowClickFunc"]) {
                                                            tableInfo["rowClickFunc"](row);
                                                        }
                                                        if (tableInfo["rowClickBgcFix"]) {
                                                            setFocusIndex(itemOffset + "-" +index);
                                                        }

                                                        clearTimeout(clickTimeOut.current);
                                                        clickTimeOut.current = null;
                                                    }, 200);
                                                }}
                                                aria-checked={isItemSelected}
                                                selected={isItemSelected}
                                            >
                                                {chkBoxExist ?
                                                    <TableCell padding="checkbox" sx={[
                                                        tableInfo["styleType"].tableHeadChkBoxCell,
                                                        {
                                                            position: (!tableInfo["fixFldCnt"] ? -1 : tableInfo["fixFldCnt"]) > 0 ? "sticky" : null,
                                                            left: 0,
                                                            backgroundColor: "inherit", // **************** 체크박스 th,td 색 분리 ***************
                                                            //backgroundColor: (!tableInfo["fixFldCnt"] ? -1 : tableInfo["fixFldCnt"]) > 0 ? "rgb(244, 248, 249)" : null
                                                        }
                                                    ]}>
                                                        <div className='w100p h100p dpFlx aiC jcC'>
                                                            <CheckBox
                                                                checked={isItemSelected}
                                                                buttonColor={AppContext.themeColor}
                                                                borderOnColor="#111"
                                                                borderColor="#999"
                                                                borderWidth={2}
                                                                onChange={(e: any) => {
                                                                    e.stopPropagation(); // 이벤트 캡쳐링 방지
                                                                    handleClick(row)
                                                                }}
                                                            />
                                                        </div>
                                                    </TableCell>
                                                : <></>}
                                                {rendering()}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                            {(filterDataArr.length === 0) && (
                                <div className='w100p h100p dpFlx aiC jcC bgcWhite' style={{height: tableInfo["bodyHeight"]}}>
                                    <Span fontFamily='fm-b' fontSize={13} letterSpacing={-0.7} color='#666'>조회할 자료가 없습니다.</Span>
                                </div>
                            )}
                        </TableContainer>

                    </div>
                    <div className={(tableInfo["footerVsb"] ? "dpFlx " : "dpNone ") + tableInfo["paginateClass"]}>
                        <div className="mw100 flx1 mb8 dpFlx">
                            <Span fontFamily="Pretendard-Light" fontSize={13} letterSpacing={-1} color="#222" style={{paddingRight: 3}}>검색결과: </Span>
                            <Span fontFamily="Pretendard-SemiBold" fontSize={13} letterSpacing={-1} color="crimson">{Util.comma(tableInfo["totalCnt"] ? tableInfo["totalCnt"] : filterDataArr.length)}</Span>
                            <Span fontFamily="Pretendard-Light" fontSize={13} letterSpacing={-1} color="#222" style={{paddingLeft: 2}}>건</Span>
                        </div>
                        {/* pageCount - 총 게시글의 개수(총 row 수)
                        pageRangeDisplayed - 한 페이지에 표시할 게시글의 수
                        marginPagesDisplayed -
                        breakLabel - 페이지 수가 많을 경우 건너뛸 수 있는 버튼
                        previousLabel - 이전페이지로 가는 버튼의 value값
                        nextLabel - 다음페이지로 가는 버튼의 value값
                        onPageChange - 페이지 버튼을 눌렀을 때 일어나는 이벤트 이를 이용해 페이지 증감
                        containerClassName - css적용할 때 사용
                        activeClassName - 현재 페이지에 css처리해주기 위한 클래스명을 적으면 됨
                        previousClassName/NextClassName - 이전/다음버튼 css적용위한 클래스명을 적으면 됨 */}
                        <StylingPaginate
                            paginatestyle={tableInfo.paginateStyle ? tableInfo.paginateStyle : {
                                buttonOnColor: AppContext.themeColor,
                                buttonWidth: 22,
                                buttonHeight: 24,
                                buttonMarginLeft: 1,
                                buttonMarginRight: 1,
                                fontSize: 12,
                                color: "#666666",
                                letterSpacing: -0.8,
                                fontFamily: "Pretendard-Light",
                                borderRadius: 3
                            }}
                        >
                            <ReactPaginate
                                breakLabel="..."
                                previousLabel={<KeyboardArrowLeftIcon sx={{ color: "#666", cursor: "pointer" }} />}
                                nextLabel={<KeyboardArrowRightIcon sx={{ color: "#666", cursor: "pointer" }} />}
                                onPageChange={handlePageClick}
                                pageRangeDisplayed={0}
                                pageCount={pageCount}
                                renderOnZeroPageCount={null}
                                forcePage={pageCount === 0 ? -1 : itemOffset}
                            />
                        </StylingPaginate>
                        {tableInfo.maxRowNums && tableInfo.maxRowNums.length > 1 ?
                            <div className='dpFlx jcFe flx1'>
                                <div className='h22 bd1 bdcCCCCCC dpFlx bdrd3'>
                                    {tableInfo.maxRowNums.map((maxRowNum: number, idx: number) => {
                                        return (
                                            <button
                                                key={idx}
                                                className={'pw8 dpFlx aiC jcC ' + (idx > 0 ? "bdl1 bdcCCCCCC" : "")}
                                                style={{
                                                    background: (itemsPerPage === maxRowNum ? (tableInfo.paginateStyle?.buttonOnColor || AppContext.themeColor) : "#fff")
                                                }}
                                                onClick={() => {
                                                    if (itemsPerPage === maxRowNum) {
                                                        return;
                                                    }
                                                    setItemsPerPage(maxRowNum);
                                                    if (tableInfo.apiFunc) {
                                                        tableInfo.apiFunc(1, maxRowNum, orderBy, order);
                                                    }
                                                    setItemOffset(0);
                                                    if (tableInfo["checkedHookParam"]) {
                                                        const setCheckDataArr = tableInfo["checkedHookParam"]["setCheckDataArr"];
                                                        setCheckDataArr([]);
                                                    }
                                                    if (scrollRef.current !== null) {
                                                        scrollRef.current.scrollTop = 0;
                                                    }
                                                }}
                                            >
                                                <Span fontFamily="Pretendard-Light" fontSize={13} letterSpacing={-1} color={itemsPerPage === maxRowNum ? "#fff" : "#454545"}>{maxRowNum}</Span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        : <></>}
                    </div>
                </Paper>
            </Box>
        );
    }
}
export default GridTable;